import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.#clearGridItemVisibilityStyles();
    requestAnimationFrame(() => {
      this.#clearGridItemVisibilityStyles();
      this.setAttribute('initialized', '');
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#clearGridItemVisibilityStyles();
    requestAnimationFrame(() => this.#clearGridItemVisibilityStyles());
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #handleFilterUpdate = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#clearGridItemVisibilityStyles());
    });
  };

  /**
   * After the product grid is replaced outside section morph (e.g. Searchanise PLP hydrate).
   */
  refreshProductGrid() {
    this.#clearGridItemVisibilityStyles();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#clearGridItemVisibilityStyles());
    });
  }

  #clearGridItemVisibilityStyles() {
    if (this.getAttribute('infinite-scroll') === 'true') return;

    this.querySelectorAll('.product-grid__item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.style.removeProperty('content-visibility');
      item.style.removeProperty('view-transition-name');
    });

    this.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
      if (!(slideshow instanceof HTMLElement)) return;

      const slides = slideshow.querySelectorAll('slideshow-slide:not([hidden])');
      if (slides.length <= 1) {
        slideshow.setAttribute('disabled', 'true');
      }

      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });
    });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
